<template>
  <span
    v-if="reasonText"
    class="balance-reason-info"
    @mouseenter="openPopover"
    @mouseleave="closePopover"
    @focusin="openPopover"
    @focusout="onFocusOut"
  >
    <span
      ref="trigger"
      class="badge bg-secondary balance-reason-info-badge"
      tabindex="0"
      role="img"
      :aria-label="reasonText"
    >
      Info
      <i class="fas fa-info-circle ms-1" aria-hidden="true"></i>
    </span>
    <span
      v-show="popoverVisible"
      ref="popover"
      class="balance-reason-info-popover"
      role="tooltip"
      @mouseenter="openPopover"
      @mouseleave="closePopover"
    >{{ reasonText }}</span>
  </span>
  <span v-else class="text-muted">-</span>
</template>

<script>
export default {
  name: "BalanceReasonInfoBadge",
  props: {
    value: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      popoverVisible: false,
      popoverStyle: {},
    };
  },
  computed: {
    reasonText() {
      const t = this.value != null ? String(this.value).trim() : "";
      return t;
    },
  },
  beforeDestroy() {
    clearTimeout(this._closeTimer);
    this.detachScrollListener();
    this.removePopoverFromBody();
  },
  methods: {
    openPopover() {
      if (!this.reasonText) return;
      clearTimeout(this._closeTimer);
      this.popoverVisible = true;
      this.$nextTick(() => {
        this.ensurePopoverInBody();
        this.updatePopoverPosition();
        this.attachScrollListener();
      });
    },
    closePopover() {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        this.popoverVisible = false;
        this.detachScrollListener();
      }, 80);
    },
    onFocusOut(event) {
      const root = this.$el;
      if (root && root.contains(event.relatedTarget)) return;
      this.closePopover();
    },
    attachScrollListener() {
      if (this._scrollListener) return;
      this._scrollListener = () => {
        if (this.popoverVisible) this.updatePopoverPosition();
      };
      window.addEventListener("scroll", this._scrollListener, true);
      window.addEventListener("resize", this._scrollListener);
    },
    detachScrollListener() {
      if (!this._scrollListener) return;
      window.removeEventListener("scroll", this._scrollListener, true);
      window.removeEventListener("resize", this._scrollListener);
      this._scrollListener = null;
    },
    ensurePopoverInBody() {
      const popover = this.$refs.popover;
      if (!popover || popover.parentNode === document.body) return;
      document.body.appendChild(popover);
    },
    removePopoverFromBody() {
      const popover = this.$refs.popover;
      if (!popover || popover.parentNode !== document.body) return;
      popover.parentNode.removeChild(popover);
    },
    updatePopoverPosition() {
      const trigger = this.$refs.trigger;
      const popover = this.$refs.popover;
      if (!trigger || !popover) return;

      const rect = trigger.getBoundingClientRect();
      const gap = 8;
      const popoverWidth = popover.offsetWidth || 260;
      const left = Math.min(
        Math.max(8, rect.left + rect.width / 2 - popoverWidth / 2),
        window.innerWidth - popoverWidth - 8
      );
      const top = Math.max(8, rect.top - gap);

      popover.style.position = "fixed";
      popover.style.top = `${top}px`;
      popover.style.left = `${left}px`;
      popover.style.transform = "translateY(-100%)";
      popover.style.zIndex = "1080";
    },
  },
};
</script>

<style scoped>
.balance-reason-info {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.balance-reason-info-badge {
  cursor: default;
  font-weight: 500;
  font-size: 0.75rem;
  padding: 0.25em 0.5em;
  user-select: none;
}

.balance-reason-info-popover {
  position: relative;
  min-width: 180px;
  max-width: 320px;
  padding: 8px 10px;
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  color: #fff;
  background: #212529;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  pointer-events: auto;
}

.balance-reason-info-popover::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #212529;
}
</style>
